import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} content - The content to sanitize
 * @param {Object} options - DOMPurify configuration options
 * @returns {string} - Sanitized content
 */
export const sanitizeHtml = (content, options = {}) => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  // Default configuration for security
  const defaultConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'span', 'p', 'br'],
    ALLOWED_ATTR: ['class', 'style'],
    FORBID_SCRIPTS: true,
    FORBID_TAGS: ['script', 'object', 'embed', 'iframe', 'form', 'input'],
    // eslint-disable-next-line no-script-url
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'javascript:'],
  };

  const config = { ...defaultConfig, ...options };
  return DOMPurify.sanitize(content, config);
};

/**
 * Sanitize plain text content (removes all HTML)
 * @param {string} content - The content to sanitize
 * @returns {string} - Plain text content
 */
export const sanitizeText = (content) => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  // Strip all HTML tags and decode HTML entities
  return DOMPurify.sanitize(content, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Validate and sanitize numeric input
 * @param {any} value - The value to validate
 * @param {Object} options - Validation options
 * @returns {number|null} - Sanitized numeric value or null if invalid
 */
export const sanitizeNumber = (value, options = {}) => {
  const { min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER, allowFloat = true } = options;
  
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const num = allowFloat ? parseFloat(value) : parseInt(value, 10);
  
  if (isNaN(num) || !isFinite(num)) {
    return null;
  }

  if (num < min || num > max) {
    return null;
  }

  return num;
};

/**
 * Sanitize user input for search queries
 * @param {string} query - The search query
 * @returns {string} - Sanitized search query
 */
export const sanitizeSearchQuery = (query) => {
  if (!query || typeof query !== 'string') {
    return '';
  }

  // Remove potentially dangerous characters for search
  return query
    .trim()
    // eslint-disable-next-line no-useless-escape
    .replace(/[<>\"'&]/g, '') // Remove HTML/JS dangerous chars
    // eslint-disable-next-line no-useless-escape
    .replace(/[\/\\]/g, '') // Remove path traversal chars
    .replace(/\s+/g, ' ') // Normalize whitespace
    .substring(0, 100); // Limit length
};

/**
 * Escape special characters for safe display
 * @param {string} text - The text to escape
 * @returns {string} - Escaped text
 */
export const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Named exports for better tree-shaking
const sanitizationUtils = {
  sanitizeHtml,
  sanitizeText,
  sanitizeNumber,
  sanitizeSearchQuery,
  escapeHtml
};

export default sanitizationUtils;